/**
 * MKS-384 — the live `Q<n>` numbering of a task's pending Factory™ questions.
 *
 * Mirrors `useTriagerProposalState`: a small per-task read that only the one
 * section calling it mounts, so the request never fires for a reader who would
 * not see the marker anyway. `enabled` carries the release-flag gate, so with
 * the flag off nothing is requested at all.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  answerPendingTaskQuestion,
  getPendingTaskQuestions,
  type AnswerPendingQuestionInput,
  type PendingTaskQuestion,
} from '../services/api/paperclipQuestions';

export const pendingTaskQuestionsKey = (taskId: number) =>
  ['pending-task-questions', taskId] as const;

export function usePendingTaskQuestions(taskId: number, enabled: boolean) {
  return useQuery<PendingTaskQuestion[]>({
    queryKey: pendingTaskQuestionsKey(taskId),
    queryFn: () => getPendingTaskQuestions(taskId),
    enabled,
    staleTime: 30 * 1000,
  });
}

/**
 * MKS-516 — answer one pending request from the task detail. Settled either way
 * (a 200 `already_answered` and a 404 both mean the list is stale): refresh the
 * list, and the thread, where the answer comment lands.
 */
export function useAnswerPendingQuestion(taskId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { requestId: number; input: AnswerPendingQuestionInput }) =>
      answerPendingTaskQuestion(taskId, args.requestId, args.input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: pendingTaskQuestionsKey(taskId) });
      void queryClient.invalidateQueries({ queryKey: ['comments', taskId] });
    },
  });
}
